import { type RefObject, useEffect, useRef } from 'react'

/**
 * Swipe right to close a right-side panel on touch screens (Apple design
 * release 3, 7 Oct 2026). The panel follows the finger (and the backdrop,
 * if given, fades with it). On release it either finishes leaving at the
 * finger's speed and then calls onClose, or settles back into place.
 *
 * Touch events (not pointer events) so the browser never cancels the
 * gesture to scroll. Vertical scrolling is untouched, and form fields,
 * canvases, anything that scrolls sideways itself and [data-no-swipe]
 * keep their own touches.
 */
export function useSwipeToClose(
  panelRef: RefObject<HTMLElement>,
  { enabled, onClose, backdropRef }: { enabled: boolean; onClose: () => void; backdropRef?: RefObject<HTMLElement> },
) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!enabled) return
    const el = panelRef.current
    if (!el) return
    let closing = false
    let drag: { id: number; x0: number; y0: number; dx: number; lastX: number; lastT: number; v: number; active: boolean; dead: boolean } | null = null

    const setOffset = (dx: number, ms = 0) => {
      const w = el.offsetWidth || 1
      const ease = 'cubic-bezier(0.2, 0.8, 0.2, 1)'
      el.style.transition = ms ? `transform ${ms}ms ${ease}` : 'none'
      el.style.transform = dx ? `translateX(${dx}px)` : ''
      const bd = backdropRef?.current
      if (bd) {
        bd.style.transition = ms ? `opacity ${ms}ms ${ease}` : 'none'
        bd.style.opacity = String(Math.max(0, 1 - dx / w))
      }
    }
    const scrollsSideways = (t: HTMLElement | null) => {
      for (let n = t; n && n !== el; n = n.parentElement) {
        const ox = getComputedStyle(n).overflowX
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return true
      }
      return false
    }
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || closing) return
      const t = e.target as HTMLElement
      if (t.closest('input, textarea, select, canvas, [data-no-swipe]') || scrollsSideways(t)) return
      const p = e.touches[0]!
      drag = { id: p.identifier, x0: p.clientX, y0: p.clientY, dx: 0, lastX: p.clientX, lastT: performance.now(), v: 0, active: false, dead: false }
    }
    const onMove = (e: TouchEvent) => {
      const d = drag
      if (!d || d.dead) return
      const p = Array.from(e.touches).find((x) => x.identifier === d.id)
      if (!p) return
      const dx = p.clientX - d.x0
      const dy = p.clientY - d.y0
      if (!d.active) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return
        // Mostly vertical, or leftwards: that's a scroll, not a swipe.
        if (dx <= 0 || Math.abs(dy) > Math.abs(dx)) {
          d.dead = true
          return
        }
        d.active = true
      }
      e.preventDefault()
      const now = performance.now()
      const dt = Math.max(1, now - d.lastT)
      d.v = 0.8 * ((p.clientX - d.lastX) / dt) + 0.2 * d.v
      d.lastX = p.clientX
      d.lastT = now
      d.dx = Math.max(0, dx)
      setOffset(d.dx)
    }
    const onEnd = (e: TouchEvent) => {
      const d = drag
      drag = null
      if (!d || !d.active) return
      const w = el.offsetWidth || window.innerWidth
      // A finger that stopped before lifting doesn't count as a fling.
      const v = performance.now() - d.lastT > 80 ? 0 : d.v
      if (e.type !== 'touchcancel' && (d.dx > w * 0.35 || v > 0.5)) {
        const ms = Math.round(Math.min(280, Math.max(120, (w - d.dx) / Math.max(v, 1.2))))
        closing = true
        setOffset(w, ms)
        window.setTimeout(() => onCloseRef.current(), ms)
      } else {
        setOffset(0, 220)
      }
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [enabled, panelRef, backdropRef])
}
